import { Button, FileDropzone, Modal, StatusPill } from '@hue/design-system';
import { useId, useRef, useState } from 'react';
import { getApiError, type BrandLogo, type BrandLogoKind } from '../lib/api.js';
import { useLogoUrl } from './brand.js';
import { LOGO_ACCEPT, logoSlots } from './brand-logo-slots.js';
import { prepareLogoTrim, type LogoTrim } from './logo-trim.js';

/// Um formato de logo: prévia sobre fundo claro e escuro, envio por clique ou arraste, troca e
/// remoção. Na unidade, `inherited` é a da empresa, mostrada enquanto a unidade não tem a própria.
export function BrandLogoSlot({
  kind,
  logo,
  inherited,
  canManage,
  onUpload,
  onRemove,
}: {
  kind: BrandLogoKind;
  logo: BrandLogo | null;
  inherited?: BrandLogo | null | undefined;
  canManage: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const slot = logoSlots.find((item) => item.kind === kind)!;
  const unitMode = inherited !== undefined;
  const shown = logo ?? inherited ?? null;
  const url = useLogoUrl(shown);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const [busy, setBusy] = useState<'upload' | 'remove'>();
  const [error, setError] = useState<string>();
  // Logo com faixa vazia em volta: quem envia compara e escolhe antes do envio.
  const [choice, setChoice] = useState<{
    original: File;
    trim: LogoTrim;
    urls: { original: string; trimmed: string };
    pick: 'trimmed' | 'original';
  } | null>(null);
  const trimFits = (trim: LogoTrim) => trim.width >= slot.minWidth && trim.height >= slot.minHeight;

  async function send(file: File | undefined) {
    if (!file || busy) return;
    setError(undefined);
    const trim = await prepareLogoTrim(file, kind === 'SYMBOL');
    if (trim) {
      setChoice({
        original: file,
        trim,
        urls: { original: URL.createObjectURL(file), trimmed: URL.createObjectURL(trim.file) },
        pick: trimFits(trim) ? 'trimmed' : 'original',
      });
      return;
    }
    await upload(file);
  }

  function closeChoice() {
    if (choice) {
      URL.revokeObjectURL(choice.urls.original);
      URL.revokeObjectURL(choice.urls.trimmed);
    }
    setChoice(null);
  }

  async function upload(file: File) {
    setBusy('upload');
    try {
      await onUpload(file);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setBusy(undefined);
    }
  }

  async function remove() {
    setError(undefined);
    setBusy('remove');
    try {
      await onRemove();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setBusy(undefined);
    }
  }

  const tag = unitMode
    ? logo
      ? { tone: 'positive' as const, text: 'Própria' }
      : { tone: 'neutral' as const, text: 'Da empresa' }
    : logo
      ? { tone: 'positive' as const, text: 'Enviada' }
      : { tone: 'neutral' as const, text: kind === 'DARK' ? 'Opcional' : 'Sem arquivo' };
  const preview =
    shown && url ? (
      <div className="grid w-full grid-cols-2 gap-2">
        <div className="grid h-24 place-items-center rounded-lg border border-[#e3e3df] bg-white p-2.5">
          <img
            src={url}
            alt={`${slot.title} sobre fundo claro`}
            className="max-h-16 max-w-full object-contain"
          />
        </div>
        <div className="grid h-24 place-items-center rounded-lg border border-[#2a2a31] bg-[#141417] p-2.5">
          <img
            src={url}
            alt={`${slot.title} sobre fundo escuro`}
            className="max-h-16 max-w-full object-contain"
          />
        </div>
      </div>
    ) : (
      <span className="text-xs text-content-muted">Carregando a prévia…</span>
    );

  return (
    <section
      aria-labelledby={titleId}
      className="grid content-start gap-3 rounded-2xl border border-surface-border p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={titleId} className="text-sm font-semibold">
            {slot.title}
          </h3>
          <p className="mt-0.5 text-xs text-content-muted">{slot.description}</p>
        </div>
        <StatusPill tone={tag.tone}>{tag.text}</StatusPill>
      </div>

      {canManage ? (
        <FileDropzone
          ref={inputRef}
          label={`Arquivo: ${slot.title}`}
          hideLabel
          multiple={false}
          files={[]}
          accept={LOGO_ACCEPT}
          disabled={Boolean(busy)}
          chooseText={busy === 'upload' ? 'Enviando…' : 'Escolher arquivo'}
          dropText="Arraste o arquivo para cá"
          hint={shown ? undefined : slot.rule}
          zoneClassName={shown ? 'grid min-h-32 place-items-center p-3' : 'min-h-32'}
          onFilesChange={(files) => void send(files[0])}
        >
          {shown ? preview : undefined}
        </FileDropzone>
      ) : (
        <div className="grid min-h-32 place-items-center rounded-xl border-[1.5px] border-surface-border bg-surface-elevated p-3 text-center">
          {shown ? preview : <span className="text-xs text-content-muted">Sem arquivo.</span>}
        </div>
      )}

      {logo && logo.sourceFormat !== 'svg' ? (
        <p className="text-xs text-content-muted">Dica: em SVG a logo fica mais nítida.</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}

      {canManage && (shown || unitMode) ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 truncate text-xs text-content-faint">
            {logo ? logo.fileName : unitMode && inherited ? 'Usando a da empresa' : ''}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              loading={busy === 'upload'}
              onClick={() => inputRef.current?.click()}
            >
              {logo ? 'Trocar' : unitMode ? 'Enviar logo própria' : 'Enviar'}
            </Button>
            {logo ? (
              <Button
                type="button"
                variant="outline"
                loading={busy === 'remove'}
                onClick={() => void remove()}
              >
                {unitMode ? 'Usar a da empresa' : 'Remover'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {choice ? (
        <Modal
          open
          onOpenChange={(open) => !open && closeChoice()}
          title={`Margens: ${slot.title}`}
          description="O arquivo tem espaço vazio em volta do desenho. Esse espaço ocupa lugar e deixa a logo pequena no topo, nos documentos e na página da empresa. Escolha como enviar."
          footer={
            <>
              <Button type="button" variant="outline" onClick={closeChoice}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const file = choice.pick === 'trimmed' ? choice.trim.file : choice.original;
                  closeChoice();
                  void upload(file);
                }}
              >
                Enviar
              </Button>
            </>
          }
        >
          <p className="mb-3 text-xs text-content-muted">
            A linha tracejada marca a borda do arquivo; o que fica entre ela e o desenho é o espaço
            vazio.
            {kind === 'SYMBOL'
              ? ' O Símbolo é sempre quadrado: o corte tira o espaço sobrando e mantém o quadrado.'
              : null}
          </p>
          <div className="grid gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
            {(
              [
                {
                  pick: 'original',
                  title: 'Original',
                  hint: 'Envia o arquivo como está, com o espaço vazio.',
                  url: choice.urls.original,
                  size: [choice.trim.originalWidth, choice.trim.originalHeight],
                  issue: null,
                },
                {
                  pick: 'trimmed',
                  title: 'Sem margens',
                  hint: 'Corta o espaço vazio: o desenho fica maior. Recomendado.',
                  url: choice.urls.trimmed,
                  size: [choice.trim.width, choice.trim.height],
                  issue: trimFits(choice.trim)
                    ? null
                    : `Sem as margens a imagem fica pequena demais: ${slot.rule}`,
                },
              ] as const
            ).map((option) => (
              <button
                key={option.pick}
                type="button"
                aria-pressed={choice.pick === option.pick}
                disabled={Boolean(option.issue)}
                onClick={() => setChoice({ ...choice, pick: option.pick })}
                className={`grid min-w-0 content-start gap-1.5 rounded-xl border-[1.5px] p-3 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-60 ${
                  choice.pick === option.pick
                    ? 'border-brand-500 bg-brand-500/10'
                    : 'border-surface-border hover:border-brand-500/60'
                }`}
              >
                {/* Tamanhos fixos em px: porcentagem dentro de grade não limita a imagem. */}
                <span className="flex h-36 items-center justify-center overflow-hidden rounded-lg border border-[#e3e3df] bg-white p-3">
                  <img
                    src={option.url}
                    alt={`${slot.title}: ${option.title.toLowerCase()}`}
                    className="block h-auto max-h-28 w-auto max-w-full outline-1 outline-offset-0 outline-dashed outline-[#a8a8a2]"
                  />
                </span>
                <strong className="font-semibold">{option.title}</strong>
                <span className="text-xs text-content-muted">{option.hint}</span>
                <span className="text-xs text-content-muted">
                  {option.size[0]} × {option.size[1]} px
                </span>
                {option.issue ? <span className="text-xs text-danger">{option.issue}</span> : null}
              </button>
            ))}
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
